import React, { useRef } from 'react'

import { Box, Callout, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Add, Delete } from '@factorialco/f0-react/icons/app'
import { CompetencyCompetencyLevelFragment } from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import i18n from 'modules/core/lib/i18n'

import {
  type FieldErrors,
  type FieldTouched,
  type LevelData,
  LevelForm,
  normalizeLevelName,
  validateLevel,
} from 'modules/competencies/components/CompetencyLevelForm'

export { type FieldErrors, type FieldTouched, type LevelData }

const createDefaultLevel = (): LevelData => {
  return { id: uuid(), name: '', description: '' }
}

export const validateLevels = (
  levels: LevelData[],
  existingLevels: CompetencyCompetencyLevelFragment[] = []
): { errors: Record<string, FieldErrors>; hasError: boolean } => {
  const errors: Record<string, FieldErrors> = {}
  const seenNames = new Set<string>(existingLevels.map((level) => level.name.trim().toLowerCase()))
  let hasError = false

  levels.forEach((level) => {
    const normalizedName = normalizeLevelName(level.name)
    const levelValidation = validateLevel(level, seenNames.has(normalizedName))
    seenNames.add(normalizedName)
    errors[level.id] = levelValidation.errors

    if (!hasError && levelValidation.hasError) {
      hasError = true
    }
  })

  return { errors, hasError }
}

interface Props {
  levels: LevelData[]
  errors?: FieldErrors[] | Record<string, FieldErrors> | string | string[] | undefined
  touched?: Record<string, FieldTouched> | FieldTouched[]
  allowAdd?: boolean
  allowDelete?: boolean
  existingLevelsCount?: number
  loading?: boolean
  onShowError?: (field: keyof LevelData, level: LevelData) => boolean
  onLevelChange: (levels: LevelData[], level: LevelData) => void
  onLevelAdd?: (levels: LevelData[], level: LevelData) => void
  onLevelDelete?: (levels: LevelData[], level: LevelData) => void
  onLevelFieldBlur?: (levelId: string | number, field: keyof LevelData) => void
}

interface AddLevelButtonProps {
  onClick: () => void
  disabled?: boolean
}

export const AddLevelButton: React.FC<AddLevelButtonProps> = ({ onClick, disabled }) => (
  <F0Button
    variant="outline"
    type="button"
    onClick={onClick}
    icon={Add}
    disabled={disabled}
    label={i18n.t('competencies_v3.levels.form.add_button')}
    size="md"
  />
)

export const CompetencyLevelsForm: React.FC<Props> = ({
  levels,
  errors,
  touched = {},
  allowAdd = true,
  allowDelete = true,
  existingLevelsCount = 0,
  loading = false,
  onShowError,
  onLevelChange,
  onLevelAdd,
  onLevelDelete,
  onLevelFieldBlur,
}) => {
  const addButtonRef = useRef<HTMLDivElement>(null)

  const handleLevelChange = (updatedLevel: LevelData) => {
    const newLevels = levels.map((level) => (level.id === updatedLevel.id ? updatedLevel : level))
    onLevelChange(newLevels, updatedLevel)
  }

  const handleAddLevel = () => {
    const newLevel = createDefaultLevel()
    const newLevels = [...levels, newLevel]
    onLevelAdd?.(newLevels, newLevel)

    // Scroll to bottom after adding the new level
    setTimeout(() => {
      addButtonRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const handleDeleteLevel = (deletedLevel: LevelData) => {
    const newLevels = levels.filter((level) => level.id !== deletedLevel.id)
    onLevelDelete?.(newLevels, deletedLevel)
  }

  const handleFieldBlur = (levelId: string | number, field: keyof LevelData) => {
    onLevelFieldBlur?.(levelId, field)
  }

  const getErrors = (index: number, levelId: string | number) => {
    if (typeof errors === 'string') {
      return errors
    }

    if (Array.isArray(errors)) {
      return errors[index]
    }

    if (typeof errors === 'object') {
      return errors[levelId]
    }

    return undefined
  }

  const getTouched = (index: number, levelId: string | number) => {
    if (Array.isArray(touched)) {
      return touched[index]
    }

    return touched[levelId]
  }

  return (
    <Box gap="s16" flexDirection="column">
      <Box gap="s8" flexDirection="column" flexNoShrink>
        <Text.Header2 color="grey1000">{i18n.t('competencies_v3.levels.form.title')}</Text.Header2>
        <Text.Normal size="300" color="grey1000">
          {i18n.t('competencies_v3.levels.form.description')}
        </Text.Normal>
      </Box>
      {existingLevelsCount > 0 && (
        <Callout
          type="info"
          title={i18n.t('competencies_v3.levels.form.existing_levels_callout.title')}
          description={i18n.t('competencies_v3.levels.form.existing_levels_callout.description', {
            count: existingLevelsCount,
          })}
        />
      )}
      {levels.length > 0 && (
        <Box gap="s16" flexDirection="column" paddingRight="s4">
          {levels.map((level, index) => {
            const position = existingLevelsCount + index + 1

            return (
              <Box gap="s8" flexDirection="row" alignItems="center" key={level.id}>
                <Box
                  flexGrow
                  flexDirection="column"
                  justifyContent="spaceBetween"
                  gap="s16"
                  background="grey200"
                  borderRadius={{ all: 'abs008' }}
                  padding="s24"
                >
                  <LevelForm
                    level={level}
                    position={position}
                    touched={getTouched(index, level.id)}
                    errors={getErrors(index, level.id)}
                    onShowError={onShowError}
                    onChange={(value) => handleLevelChange(value)}
                    onBlur={(field) => handleFieldBlur(level.id, field)}
                  />
                </Box>
                {allowDelete && (
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    minWidth="s32"
                    justifyContent="flexEnd"
                  >
                    <F0Button
                      variant="critical"
                      type="button"
                      icon={Delete}
                      onClick={() => handleDeleteLevel(level)}
                      hideLabel
                      disabled={loading}
                      label={i18n.t('competencies_v3.levels.form.delete_button', {
                        name: level.name,
                      })}
                    />
                  </Box>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      {allowAdd && (
        <Box flexDirection="row" ref={addButtonRef}>
          <AddLevelButton onClick={handleAddLevel} disabled={loading} />
        </Box>
      )}
    </Box>
  )
}
